import { cn } from '@workspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { ArrowRight, Loader2 } from 'lucide-react'
import type * as React from 'react'
import { dsmButtonBase } from './button-base.js'

/**
 * The supporting action — everything that is not the one primary CTA.
 *
 * Same geometry as `CtaButton` (they line up when placed side by side), only
 * quieter. Three tones, each earning its place:
 * - `neutral` the normal case
 * - `danger`  the product's only red, for destroying or disconnecting
 * - `success` the product's only green, for turning something back on
 *
 * `success` exists because pause/resume sit in the same slot and swap: the
 * colour is what tells them apart at a glance, and the alternative was the
 * same emerald class string hand-copied into four call sites. A "tonal orange"
 * tone was tried and removed — it read as a dimmer primary, which is a job
 * `CtaButton` already has.
 */
const secondaryButtonVariants = cva('', {
  variants: {
    tone: {
      neutral: 'border border-border bg-input text-foreground hover:bg-hover',
      danger:
        'border border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20',
      success:
        'border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/15',
    },
  },
  defaultVariants: {
    tone: 'neutral',
  },
})

type BaseVariants = VariantProps<typeof dsmButtonBase>

export interface SecondaryButtonProps
  extends React.ComponentProps<'button'>,
    BaseVariants,
    VariantProps<typeof secondaryButtonVariants> {
  /** Icon shown before the label. */
  icon?: React.ReactNode
  /** Trailing arrow, for actions that move the user somewhere. */
  arrow?: boolean
  /** Swaps the leading icon for a spinner and disables the button. */
  loading?: boolean
}

export function SecondaryButton({
  className,
  tone,
  size,
  fullWidth,
  icon,
  arrow,
  loading,
  disabled,
  type = 'button',
  children,
  ...props
}: SecondaryButtonProps) {
  return (
    <button
      data-slot="secondary-button"
      type={type}
      className={cn(
        dsmButtonBase({ size, fullWidth }),
        secondaryButtonVariants({ tone }),
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" /> : icon}
      <span>{children}</span>
      {arrow && <ArrowRight />}
    </button>
  )
}

/** Classes for a secondary action that has to be an anchor or router `Link`. */
export function secondaryLinkClasses({
  tone,
  size,
  fullWidth,
}: BaseVariants & VariantProps<typeof secondaryButtonVariants> = {}) {
  return cn(dsmButtonBase({ size, fullWidth }), secondaryButtonVariants({ tone }))
}
